<template>
  <Modal
    v-model="isVisible"
    :closable="closable"
    :mask-closable="maskClosable"
    :width="width"
    class="modal"
    :draggable="draggable"
    :scrollable="scrollable"
    @on-visible-change="onCloseModal"
  >
    <div slot="header">
      <div
        v-if="title"
        class="modal-title"
      >
        <p>
          <i
            v-if="!category && icon"
            :class="titleIcon"
          />
          <span :class="`description ${customType}`">
            {{ title }}
          </span>
        </p>
      </div>
    </div>

    <div
      v-if="category"
      class="main-container"
    >
      <div
        v-if="showIcon"
        class="icon-container"
      >
        <i :class="iconClass" />
      </div>
      <div class="body-container">
        <div
          v-if="message"
          class="modal-content"
        >
          <p v-dompurify-html="message" />
        </div>
        <slot name="content" />
      </div>
    </div>

    <template v-else>
      <p
        v-if="message"
        v-dompurify-html="message"
      />
      <slot name="content" />
    </template>

    <div slot="footer">
      <div
        v-if="showFooter"
        class="modal-buttons"
      >
        <Button
          v-for="(button, index) in actionButtons"
          :key="index"
          :type="button.type || 'success'"
          size="large"
          :style="actionButtonStyle(button.color)"
          :disabled="isDisabled(button)"
          class="button"
          @click="onActionClick(button)"
        >
          {{ button.label }}
        </Button>
        <Button
          v-if="showCancelButton"
          type="default"
          size="large"
          :style="actionButtonStyle(cancelButton.color)"
          class="button"
          @click="cancelAction"
        >
          {{ cancelButton.label }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script>
import _ from 'lodash'

export default {
  model: {
    prop: 'value',
    event: 'change'
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    closable: {
      type: Boolean,
      default: true
    },
    maskClosable: {
      type: Boolean,
      default: false
    },
    category: {
      type: String,
      default: ''
    },
    customType: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    formName: {
      type: String,
      default: ''
    },
    actionButtons: {
      type: Array,
      default: () => []
    },
    cancelButton: {
      type: Object,
      default: () => {}
    },
    autoClose: {
      type: Number,
      default: 0
    },
    isFormValid: {
      type: Boolean,
      default: false
    },
    width: {
      type: [Number, String],
      default: 520
    },
    draggable: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isVisible: this.value
    }
  },

  computed: {
    showCancelButton () {
      return !_.isEmpty(this.cancelButton)
    },

    titleIcon () {
      return `${this.icon} icon`
    },

    showIcon () {
      return this.category !== 'custom' || this.icon
    },

    iconClass () {
      switch (this.category) {
        case 'success':
          return 'fas fa-check-circle big'
        case 'warning':
          return 'fas fa-exclamation-triangle big'
        case 'error':
          return 'fas fa-times-circle big'
        case 'custom':
          return `${this.icon} big`
        default:
          return 'fas fa-info-circle big'
      }
    },

    showFooter () {
      return !_.isEmpty(this.actionButtons) || this.showCancelButton
    }
  },

  watch: {
    value (newStatus) {
      this.isVisible = newStatus
    },

    autoClose (timeout) {
      if (!timeout) {
        return
      }

      setTimeout(() => {
        this.$emit('closeModal')
        this.$emit('change', false)
      }, timeout * 1000)
    }
  },

  methods: {
    /**
     * Evokes when the action button is clicked
     * @param {Object} actionButton - The action button that has been clicked
     */
    onActionClick (actionButton) {
      if (actionButton.callback) {
        actionButton.callback()
      } else {
        this.emitAction(actionButton.action)
      }
    },

    /**
     * Notifies the parent that an action button has been triggered.
     * @param {String} action
     */
    emitAction (action) {
      this.$emit('onButtonClick', action)
    },

    /**
     * Triggers the Cancel action if the modal was closed by the "close" icon or by pressing "ESC" key.
     * @param {Boolean} isVisible
     */
    onCloseModal (isVisible = false) {
      if (!isVisible) {
        this.cancelAction()
      }
    },

    /**
     * Notifices the parent that the modal should be closed.
     */
    cancelAction () {
      const cancelButton = this.cancelButton || {}
      if (cancelButton.callback) {
        cancelButton.callback()
      } else if (cancelButton.action) {
        this.emitAction(cancelButton.action)
      }

      this.$emit('closeModal')
      this.$emit('change', false)
    },

    /**
     * Verifies if the action buttons restricted by form (all required items are valid) should be
     * "enabled" / "disabled"
     * @param {Object} button
     * @return {Boolean}
     */
    isDisabled (button) {
      return button.restrictedByFormRules && !this.isFormValid
    },

    /**
     * Defines the inline styling colors for an action button.
     * @param {Object} color
     * @return {Object}
     */
    actionButtonStyle (color = {}) {
      if (_.isEmpty(color)) {
        return
      }

      const {
        font,
        background
      } = color
      const border = color.border || background

      return {
        color: font || '',
        backgroundColor: background || '',
        borderColor: border || ''
      }
    }
  }
}
</script>

<style scoped>
  .modal-title {
    padding: 8px 16px;
    border-bottom: 1px solid var(--color-gray-border);
    text-align: center;
  }

  .modal-title .icon,
  .modal-title .description {
    font-size: 16px;
  }

  .modal-title .description.warning {
    color: var(--color-red-alert);
    text-align: center
  }

  .modal-content {
    font-size: 14px;
  }

  .modal-buttons {
    padding: 8px 16px;
    border-top: 1px solid var(--color-gray-border);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .modal-buttons .button {
    min-width: 80px;
    padding: 8px;
  }

  .modal >>> .ivu-modal-header,
  .modal >>> .ivu-modal-footer {
    margin: 0;
    padding: 0;
    border: none;
  }

  .main-container {
    width: 100%;
    display: table;
    flex-direction: row;
    align-items: center;
  }

  .main-container .icon-container {
    width: 40px;
    display: table-cell;
    vertical-align: middle;
  }

  .main-container .icon-container .big {
    margin-right: 8px;
    font-size: 32px;
    width: 32px;
    height: 32px;
    text-align: center;
  }

  .main-container .body-container {
    width: calc(100% - 40px);
    display: table-cell;
    vertical-align: top;
  }

  .main-container .body-container .modal-title {
    padding: 0 8px;
    font-weight: bold;
    text-align: left;
  }

  .main-container .body-container .modal-content {
    padding: 8px;
  }

  .fas.fa-info-circle.big {
    color: var(--color-info);
  }

  .fas.fa-check-circle.big {
    color: var(--color-success);
  }

  .fas.fa-exclamation-triangle.big {
    color: var(--color-warning);
  }

  .fas.fa-times-circle.big {
    color: var(--color-red-border);
  }
</style>
